import { Info } from "lucide-react";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

export type ObjectiveType = "committed" | "aspirational";

export const OBJECTIVE_TYPE_META: Record<
  ObjectiveType,
  { label: string; description: string }
> = {
  committed: {
    label: "Committed",
    description: "You expect to fully achieve this. Missing it is a problem.",
  },
  aspirational: {
    label: "Aspirational",
    description: "A moonshot. 70% achieved is a win.",
  },
};

// --- Radio cards (wizard setup step) ---

interface ObjectiveTypeRadioCardsProps {
  value: ObjectiveType | null;
  onChange: (value: ObjectiveType) => void;
}

export function ObjectiveTypeRadioCards({ value, onChange }: ObjectiveTypeRadioCardsProps) {
  return (
    <div className="space-y-2">
      <Label>Type</Label>
      <div className="grid grid-cols-2 gap-3">
        {(Object.entries(OBJECTIVE_TYPE_META) as [ObjectiveType, { label: string; description: string }][]).map(
          ([type, meta]) => (
            <button
              key={type}
              type="button"
              role="radio"
              aria-checked={value === type}
              onClick={() => onChange(type)}
              className={cn(
                "rounded-lg border p-4 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]",
                value === type
                  ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/5 ring-1 ring-[hsl(var(--primary))]"
                  : "border-[hsl(var(--border))] hover:border-[hsl(var(--primary))]/50 hover:bg-[hsl(var(--muted))]",
              )}
            >
              <div className="flex items-center gap-2">
                <span
                  className={cn(
                    "mt-0.5 h-4 w-4 shrink-0 rounded-full border-2",
                    value === type
                      ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))]"
                      : "border-[hsl(var(--muted-foreground))]",
                  )}
                />
                <span className="font-medium text-sm">{meta.label}</span>
              </div>
              <p className="mt-2 text-xs text-[hsl(var(--muted-foreground))] leading-snug pl-6">
                {meta.description}
              </p>
            </button>
          ),
        )}
      </div>
    </div>
  );
}

// --- Select with tooltip (create dialog + inline editor) ---

interface ObjectiveTypeSelectProps {
  value: ObjectiveType;
  defaultValue?: ObjectiveType;
  onChange: (value: ObjectiveType) => void;
}

export function ObjectiveTypeSelect({ value, defaultValue, onChange }: ObjectiveTypeSelectProps) {
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-1.5">
        <Label>Type</Label>
        <Tooltip>
          <TooltipTrigger asChild>
            <Info className="h-3.5 w-3.5 cursor-help text-[hsl(var(--muted-foreground))]" />
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-xs space-y-1.5 p-3">
            {(Object.entries(OBJECTIVE_TYPE_META) as [ObjectiveType, { label: string; description: string }][]).map(
              ([type, meta]) => (
                <p key={type} className="text-xs">
                  <span className="font-semibold">{meta.label}</span> — {meta.description}
                </p>
              ),
            )}
          </TooltipContent>
        </Tooltip>
      </div>
      <Select
        value={value}
        defaultValue={defaultValue}
        onValueChange={(v) => onChange(v as ObjectiveType)}
      >
        <SelectTrigger>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="committed">Committed</SelectItem>
          <SelectItem value="aspirational">Aspirational</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
}
